class BacneProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.bacne-product-gallery__track');
    this.slides = this.querySelectorAll('.bacne-product-gallery__slide');
    this.thumbs = this.closest('media-gallery')?.querySelectorAll('[data-bacne-thumb]') || [];
    this.thumbsContainer = this.closest('media-gallery')?.querySelector('.bacne-product-gallery__thumbs');
    this.prevBtn = this.querySelector('[data-bacne-gallery-prev]');
    this.nextBtn = this.querySelector('[data-bacne-gallery-next]');
    this.total = parseInt(this.dataset.slideCount, 10) || this.slides.length;
    this.hintDismissed = false;
    this.currentIndex = 0;

    if (!this.track) return;

    this.onScroll = this.handleScroll.bind(this);
    this.onThumbClickHandler = this.onThumbClick.bind(this);
    this.onPrevClick = () => this.goTo(this.currentIndex - 1);
    this.onNextClick = () => this.goTo(this.currentIndex + 1);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClickHandler));
    this.prevBtn?.addEventListener('click', this.onPrevClick);
    this.nextBtn?.addEventListener('click', this.onNextClick);

    this.setActiveIndex(this.total >= 2 ? this.getCenteredIndex() : 0, { scrollThumb: false });
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClickHandler));
    this.prevBtn?.removeEventListener('click', this.onPrevClick);
    this.nextBtn?.removeEventListener('click', this.onNextClick);
  }

  dismissScrollHint() {
    if (this.hintDismissed) return;
    this.hintDismissed = true;
    this.classList.add('is-interacted');
  }

  handleScroll() {
    this.setActiveIndex(this.getCenteredIndex());
    this.dismissScrollHint();
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.total - 1));
    const slide = this.slides[clamped];
    if (!slide || !this.track) return;
    this.dismissScrollHint();
    slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    this.setActiveIndex(clamped);
  }

  onThumbClick(event) {
    this.goTo(parseInt(event.currentTarget.dataset.index, 10));
  }

  setActiveIndex(index, { scrollThumb = true } = {}) {
    this.currentIndex = index;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.thumbs.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('is-active', isActive);
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
        if (scrollThumb) this.scrollThumbIntoView(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    if (this.prevBtn) this.prevBtn.disabled = this.currentIndex <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= this.total - 1;
  }

  scrollThumbIntoView(thumb) {
    const container = this.thumbsContainer;
    if (!container || !thumb) return;
    const thumbLeft = thumb.offsetLeft;
    const thumbRight = thumbLeft + thumb.offsetWidth;
    const visibleLeft = container.scrollLeft;
    const visibleRight = visibleLeft + container.clientWidth;
    if (thumbLeft < visibleLeft) {
      container.scrollTo({ left: thumbLeft, behavior: 'smooth' });
    } else if (thumbRight > visibleRight) {
      container.scrollTo({ left: thumbRight - container.clientWidth, behavior: 'smooth' });
    }
  }

  getCenteredIndex() {
    const trackRect = this.track.getBoundingClientRect();
    const center = trackRect.left + trackRect.width / 2;
    let closest = 0;
    let minDist = Infinity;
    this.slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const dist = Math.abs(center - (rect.left + rect.width / 2));
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    return closest;
  }
}

if (!customElements.get('bacne-product-gallery')) {
  customElements.define('bacne-product-gallery', BacneProductGallery);
}

(function () {
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    var number = (cents / 100).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    var fraction = precision > 0 ? decimal + parts[1] : '';
    if (precision > 0 && /^0+$/.test(parts[1])) fraction = '';
    return dollars + fraction;
  }

  function formatMoney(cents, format) {
    var formatString = format || '₪{{amount}}';
    var match = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    var value = formatWithDelimiters(cents, 2);
    if (match) {
      switch (match[1]) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', ',');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }
      return formatString.replace(/\{\{\s*\w+\s*\}\}/, value);
    }
    return '₪' + value;
  }

  function cartRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (root) return root;
    var cartUrl = window.routes && window.routes.cart_url;
    if (cartUrl && cartUrl.indexOf('/cart') !== -1) return cartUrl.slice(0, cartUrl.indexOf('/cart') + 1);
    return '/';
  }

  function cartEndpoint(path) {
    var routes = window.routes || {};
    if (path === 'add' && routes.cart_add_url) return routes.cart_add_url + '.js';
    if (path === 'change' && routes.cart_change_url) return routes.cart_change_url + '.js';
    if (path === 'update' && routes.cart_update_url) return routes.cart_update_url + '.js';
    return cartRoot() + 'cart' + (path ? '/' + path : '') + '.js';
  }

  function readJson(response) {
    return response.json().then(function (data) {
      if (!response.ok) throw new Error('cart');
      return data;
    });
  }

  function getCart() {
    return fetch(cartEndpoint(''), { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(readJson);
  }

  function postCart(path, body) {
    return fetch(cartEndpoint(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(readJson);
  }

  function discountCodes(cart) {
    return (cart && cart.discount_codes ? cart.discount_codes : []).filter(function (entry) {
      return entry && entry.code;
    });
  }

  function collectApplications(cart) {
    var apps = (cart.cart_level_discount_applications || []).slice();
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        if (allocation && allocation.discount_application) apps.push(allocation.discount_application);
      });
    });
    return apps.filter(function (app) {
      return app && app.type === 'discount_code';
    });
  }

  function formatPercent(value) {
    var n = parseFloat(value);
    if (!isFinite(n) || n <= 0) return '';
    var rounded = Math.round(n * 100) / 100;
    return String(rounded).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1') + '%';
  }

  function fixedDiscountCents(application) {
    var raw = parseFloat(application.value);
    if (!isFinite(raw) || raw <= 0) return 0;
    var allocated = Number(application.total_allocated_amount);
    if (isFinite(allocated) && allocated > 0 && Math.round(raw) === allocated) return allocated;
    return Math.round(raw * 100);
  }

  function describeDiscount(application, moneyFormat) {
    if (!application) return '';
    if (application.target_type === 'shipping_line') return 'משלוח חינם';
    if (application.value_type === 'percentage') return formatPercent(application.value);
    if (application.value_type === 'fixed_amount') {
      var cents = fixedDiscountCents(application);
      return cents > 0 ? formatMoney(cents, moneyFormat) : '';
    }
    return '';
  }

  function offerForCode(cart, code, moneyFormat) {
    var upper = String(code).toUpperCase();
    var apps = collectApplications(cart);
    var match = apps.filter(function (app) {
      return String(app.title || '').toUpperCase() === upper || String(app.key || '').toUpperCase() === upper;
    });
    if (!match.length && discountCodes(cart).length === 1) match = apps;
    var amounts = [];
    match.forEach(function (app) {
      var label = describeDiscount(app, moneyFormat);
      if (label && amounts.indexOf(label) === -1) amounts.push(label);
    });
    if (!amounts.length) return null;
    return { code: code, amount: amounts.join(' ו') };
  }

  function offersFromCart(cart, moneyFormat) {
    var seen = {};
    var offers = [];
    discountCodes(cart).forEach(function (entry) {
      if (cart.item_count > 0 && entry.applicable === false) return;
      var key = String(entry.code).toUpperCase();
      if (seen[key]) return;
      var offer = offerForCode(cart, entry.code, moneyFormat);
      if (!offer) return;
      seen[key] = true;
      offers.push(offer);
    });
    return offers;
  }

  function renderDiscountBanner(banner, offers) {
    if (!banner) return;
    banner.textContent = '';
    if (!offers || !offers.length) {
      banner.hidden = true;
      return;
    }
    var offer = offers[0];
    var badge = document.createElement('span');
    badge.className = 'shoulder-code__badge';
    var sweep = document.createElement('span');
    sweep.className = 'shoulder-code__sweep';
    sweep.setAttribute('aria-hidden', 'true');
    var disc = document.createElement('span');
    disc.className = 'shoulder-code__disc';
    disc.textContent = offer.amount;
    var copy = document.createElement('span');
    copy.className = 'shoulder-code__copy';
    var kicker = document.createElement('span');
    kicker.className = 'shoulder-code__kicker';
    kicker.textContent = 'קיבלת הנחה';
    var code = document.createElement('bdi');
    code.className = 'shoulder-code__code';
    code.dir = 'ltr';
    code.textContent = offers.map(function (item) { return item.code; }).join(' · ');
    copy.append(kicker, code);
    badge.append(sweep, disc, copy);
    var note = document.createElement('span');
    note.className = 'shoulder-code__note';
    note.textContent = 'ההנחה תחושב בקופה';
    banner.append(badge, note);
    banner.hidden = false;
  }

  function addedLine(payload, variantId) {
    var items = payload && payload.items ? payload.items : [];
    if (!items.length && payload && payload.key) items = [payload];
    var match = items.find(function (item) {
      return String(item.variant_id || item.id) === String(variantId);
    });
    return match || items[0] || null;
  }

  function codeKey(cart) {
    return discountCodes(cart).map(function (entry) {
      return String(entry.code).toUpperCase();
    }).sort().join('|');
  }

  function readOfferCache(cart) {
    try {
      var saved = JSON.parse(sessionStorage.getItem('shoulder-discount-offers') || '');
      if (!saved || saved.key !== codeKey(cart) || !Array.isArray(saved.offers)) return null;
      return saved.offers;
    } catch (error) {
      return null;
    }
  }

  function writeOfferCache(cart, offers) {
    try {
      sessionStorage.setItem('shoulder-discount-offers', JSON.stringify({ key: codeKey(cart), offers: offers }));
    } catch (error) {}
  }

  // An empty cart has the code but not its rate. Price one unit, read the rate, then remove it.
  function measureEmptyCart(variantId) {
    var lineKey = '';
    return postCart('add', { items: [{ id: Number(variantId), quantity: 1 }] })
      .then(function (payload) {
        var line = addedLine(payload, variantId);
        lineKey = line && line.key ? line.key : '';
        return getCart();
      })
      .finally(function () {
        return getCart()
          .then(function (latest) {
            var lines = (latest.items || []).filter(function (item) {
              return String(item.variant_id) === String(variantId);
            });
            var line = lines.find(function (item) { return item.key === lineKey; });
            if (!line && lines.length === 1) line = lines[0];
            if (!line) return null;
            return postCart('change', { id: line.key, quantity: Math.max(0, Number(line.quantity) - 1) });
          })
          .catch(function () {});
      });
  }

  function initDiscountBanner(root) {
    var banner = root.querySelector('[data-shoulder-discount]');
    var variantId = root.dataset.variantId;
    var moneyFormat = root.dataset.moneyFormat;
    if (!banner || root.dataset.shoulderDiscountReady === 'true') return;
    root.dataset.shoulderDiscountReady = 'true';

    var reading = false;
    var queued = false;

    function refresh() {
      if (reading) {
        queued = true;
        return;
      }
      reading = true;
      var urlCode = '';
      try {
        urlCode = (new URLSearchParams(window.location.search).get('discount') || '').trim();
      } catch (error) {
        urlCode = '';
      }

      getCart()
        .then(function (cart) {
          var codes = discountCodes(cart).map(function (entry) { return entry.code; });
          var hasUrlCode = urlCode && codes.some(function (code) {
            return code.toUpperCase() === urlCode.toUpperCase();
          });
          if (urlCode && !hasUrlCode) {
            return postCart('update', { discount: codes.concat(urlCode).join(',') });
          }
          return cart;
        })
        .then(function (cart) {
          var offers = offersFromCart(cart, moneyFormat);
          if (offers.length || cart.item_count > 0 || !discountCodes(cart).length || !variantId) return offers;
          var cached = readOfferCache(cart);
          if (cached) return cached;
          return measureEmptyCart(variantId).then(function (measured) {
            var measuredOffers = offersFromCart(measured, moneyFormat).filter(function (offer) {
              return discountCodes(measured).some(function (entry) {
                return entry.applicable !== false && entry.code.toUpperCase() === offer.code.toUpperCase();
              });
            });
            writeOfferCache(cart, measuredOffers);
            return measuredOffers;
          });
        })
        .then(function (offers) {
          renderDiscountBanner(banner, offers || []);
        })
        .catch(function () {
          banner.hidden = true;
        })
        .finally(function () {
          reading = false;
          if (queued) {
            queued = false;
            refresh();
          }
        });
    }

    refresh();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    }
  }

  function initBuy(root) {
    if (!root || root.dataset.shoulderReady === 'true') return;
    root.dataset.shoulderReady = 'true';

    var unitCents = Number(root.dataset.priceCents) || 0;
    var saveCents = Number(root.dataset.saveCents) || 0;
    var moneyFormat = root.dataset.moneyFormat;
    var input = root.querySelector('[data-shoulder-qty]');
    var total = root.querySelector('[data-shoulder-total]');
    var saveNodes = document.querySelectorAll('[data-shoulder-save], [data-shoulder-sticky-save]');
    var stickyPrice = document.querySelector('[data-shoulder-sticky-price]');
    var min = input ? Number(input.min) || 1 : 1;
    var max = input ? Number(input.max) || 5 : 5;

    function render() {
      if (!input) return;
      var qty = Math.min(max, Math.max(min, Number(input.value) || min));
      input.value = qty;
      if (total) total.textContent = formatMoney(unitCents * qty, moneyFormat);
      if (stickyPrice) stickyPrice.textContent = formatMoney(unitCents * qty, moneyFormat);
      if (saveCents > 0) {
        var saved = 'חיסכון של ' + formatMoney(saveCents * qty, moneyFormat);
        saveNodes.forEach(function (node) {
          node.textContent = saved;
        });
      }
    }

    root.querySelectorAll('[data-shoulder-qty-btn]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (!input) return;
        var next = Number(input.value) + Number(button.dataset.shoulderQtyBtn);
        input.value = Math.min(max, Math.max(min, next));
        render();
      });
    });

    if (input) {
      input.addEventListener('change', render);
      render();
    }

    var stickyButton = document.querySelector('[data-shoulder-submit]');
    var submit = root.querySelector('[data-shoulder-atc]');
    if (stickyButton && submit) {
      stickyButton.addEventListener('click', function () {
        submit.click();
      });
    }

    var offer = root.querySelector('[data-shoulder-offer]');
    var sticky = document.querySelector('[data-shoulder-sticky]');
    initDiscountBanner(root);

    if (offer && sticky && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            sticky.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { threshold: 0.4 }
      );
      observer.observe(offer);
    }
  }

  function initReviews(root) {
    if (!root || root.dataset.shoulderReviewsReady === 'true') return;
    var track = root.querySelector('.shoulder-reviews__grid');
    var cards = track ? Array.prototype.slice.call(track.children) : [];
    if (!track || cards.length < 2) return;
    root.dataset.shoulderReviewsReady = 'true';

    var prev = root.querySelector('[data-shoulder-reviews-prev]');
    var next = root.querySelector('[data-shoulder-reviews-next]');
    var index = 0;

    function closestIndex() {
      var trackRect = track.getBoundingClientRect();
      var center = trackRect.left + trackRect.width / 2;
      var best = 0;
      var bestDist = Infinity;
      cards.forEach(function (card, i) {
        var rect = card.getBoundingClientRect();
        var dist = Math.abs(rect.left + rect.width / 2 - center);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    function updateButtons() {
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= cards.length - 1;
    }

    function go(to) {
      index = Math.max(0, Math.min(cards.length - 1, to));
      cards[index].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      updateButtons();
    }

    if (prev) {
      prev.addEventListener('click', function () {
        go(closestIndex() - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        go(closestIndex() + 1);
      });
    }

    var frame = 0;
    track.addEventListener('scroll', function () {
      if (frame) return;
      frame = requestAnimationFrame(function () {
        index = closestIndex();
        updateButtons();
        frame = 0;
      });
    }, { passive: true });

    updateButtons();
  }

  function initStepVideos(scope) {
    var videos = (scope || document).querySelectorAll('.shoulder-step video, .shoulder-feature video, .shoulder-science video');
    if (!videos.length) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    videos.forEach(function (video) {
      video.muted = true;
      if (reduce) {
        video.removeAttribute('autoplay');
        video.pause();
      }
    });
    if (reduce || !('IntersectionObserver' in window)) {
      if (!reduce) videos.forEach(function (video) { video.play().catch(function () {}); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) entry.target.play().catch(function () {});
        else entry.target.pause();
      });
    }, { threshold: 0.4 });
    videos.forEach(function (video) { observer.observe(video); });
  }

  function initJourney(scope) {
    (scope || document).querySelectorAll('[data-shoulder-journey]').forEach(function (root) {
      if (root.dataset.shoulderJourneyReady === 'true') return;
      root.dataset.shoulderJourneyReady = 'true';

      var steps = Array.prototype.slice.call(root.querySelectorAll('[data-shoulder-journey-step]'));
      var nodes = steps.map(function (step) { return step.querySelector('.shoulder-journey__node'); }).filter(Boolean);
      var reached = {};
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function activate(index, animate) {
        if (reached[index]) return;
        reached[index] = true;
        var step = steps[index];
        if (!step) return;
        if (index > 0) {
          var previous = steps[index - 1];
          if (!animate) {
            var fill = previous.querySelector('.shoulder-journey__connector-fill');
            if (fill) fill.style.transition = 'none';
          }
          previous.classList.add('is-linked');
        }
        var reveal = function () {
          if (!animate) {
            step.querySelectorAll('.shoulder-journey__body, .shoulder-journey__points li').forEach(function (el) {
              el.style.transition = 'none';
            });
          }
          step.classList.add('is-active');
        };
        if (!animate || index === 0) reveal();
        else window.setTimeout(reveal, 180);
      }

      if (reduce || !('IntersectionObserver' in window)) {
        root.classList.add('is-ready');
        steps.forEach(function (_, index) { activate(index, false); });
        return;
      }

      root.classList.add('is-ready');
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var step = entry.target.closest('[data-shoulder-journey-step]');
          var index = steps.indexOf(step);
          if (index === -1 || reached[index]) return;
          activate(index, true);
        });
      }, { threshold: 0.55, rootMargin: '0px 0px -22% 0px' });

      nodes.forEach(function (node) { observer.observe(node); });

      requestAnimationFrame(function () {
        var first = nodes[0];
        if (!first) return;
        if (first.getBoundingClientRect().top < window.innerHeight * 0.78) activate(0, true);
      });
    });
  }

  function initAtcTriggers(scope) {
    var submit = document.querySelector('[data-shoulder-atc]');
    if (!submit) return;
    (scope || document).querySelectorAll('[data-shoulder-atc-trigger]').forEach(function (button) {
      if (button.dataset.shoulderAtcReady === 'true') return;
      button.dataset.shoulderAtcReady = 'true';
      button.addEventListener('click', function () {
        submit.click();
      });
    });
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-shoulder-buy]').forEach(initBuy);
    (scope || document).querySelectorAll('[data-shoulder-reviews]').forEach(initReviews);
    initAtcTriggers(scope);
    initJourney(scope);
    initStepVideos(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
